/**
 * Google Sheets User Repository
 *
 * Google Sheets API를 사용하여 실제 스프레드시트에서 사용자 데이터를 관리합니다.
 */

import type { User } from '../types/User'
import type { UserRepository } from './UserRepository'
import { GoogleSheetsClient } from '@/core/api/GoogleSheetsClient'
import { SHEET_NAMES } from '@/core/config/constants'
import { getWorkspaceClient } from '@/core/workspace/WorkspaceContext'

/**
 * Spreadsheet 행을 User 객체로 변환
 *
 * 첫 행은 헤더이므로 2행부터 데이터입니다.
 * 컬럼 순서: id, email, name, role, isActive, joinedAt
 */
function rowToUser(row: string[]): User {
  return {
    id: row[0] || '',
    email: row[1] || '',
    name: row[2] || '',
    role: (row[3] || 'member') as 'owner' | 'member',
    isActive: row[4] === 'true' || row[4] === 'TRUE',
    joinedAt: row[5] || '',
  }
}

/**
 * User 객체를 Spreadsheet 행으로 변환
 *
 * 컬럼 순서: id, email, name, role, isActive, joinedAt
 */
function userToRow(user: User): string[] {
  return [
    user.id,
    user.email,
    user.name,
    user.role,
    user.isActive.toString(),
    user.joinedAt,
  ]
}

/**
 * Google Sheets User Repository 구현
 */
export class GoogleSheetsUserRepository implements UserRepository {
  constructor(_client?: GoogleSheetsClient) {
    // client 파라미터는 테스트 호환성을 위해 유지하지만 사용하지 않음
  }

  /**
   * 현재 워크스페이스의 GoogleSheetsClient 가져오기
   */
  private getClient(): GoogleSheetsClient {
    return getWorkspaceClient()
  }

  /**
   * 모든 사용자 조회
   */
  async findAll(): Promise<User[]> {
    // 시트에서 모든 데이터 읽기
    const rows = await this.getClient().readSheet(SHEET_NAMES.USERS)

    // 첫 행은 헤더이므로 제외
    const dataRows = rows.slice(1)

    // 각 행을 User 객체로 변환
    return dataRows
      .filter((row) => row.length > 0) // 빈 행 제외
      .map(rowToUser)
  }

  /**
   * 활성화된 사용자만 조회
   *
   * 참고: "공용" 특수 사용자를 항상 맨 앞에 추가합니다.
   * 이 사용자는 Google Sheets에 저장되지 않고 애플리케이션 레벨에서만 존재합니다.
   */
  async findActive(): Promise<User[]> {
    // 실제 사용자 조회
    const all = await this.findAll()
    const realUsers = all.filter((user) => user.isActive)

    // "공용" 특수 사용자 (항상 맨 앞에 추가)
    const sharedUser: User = {
      id: 'user_shared',
      email: 'shared@ourwallet',
      name: '공용',
      role: 'member',
      isActive: true,
      joinedAt: '2024-01-01T00:00:00',
    }

    return [sharedUser, ...realUsers]
  }

  /**
   * ID로 사용자 조회
   */
  async findById(id: string): Promise<User | null> {
    const all = await this.findAll()
    return all.find((user) => user.id === id) || null
  }

  /**
   * 이메일로 사용자 조회
   */
  async findByEmail(email: string): Promise<User | null> {
    const all = await this.findAll()
    return all.find((user) => user.email === email) || null
  }

  /**
   * 새 사용자 생성
   *
   * @todo Phase 2에서 구현 예정
   */
  async create(_input: Omit<User, 'id'>): Promise<User> {
    throw new Error('Not implemented: User creation will be implemented in Phase 2')
  }

  /**
   * 사용자 정보 수정
   *
   * 특정 사용자의 정보를 업데이트합니다.
   * Google Sheets의 해당 행을 찾아서 수정된 값으로 업데이트합니다.
   */
  async update(id: string, input: Partial<User>): Promise<User> {
    // 1. 전체 데이터 읽기
    const rows = await this.getClient().readSheet(SHEET_NAMES.USERS)
    const dataRows = rows.slice(1)

    // 2. 해당 행 찾기
    const rowIndex = dataRows.findIndex((row) => row[0] === id)
    if (rowIndex === -1) {
      throw new Error(`사용자를 찾을 수 없습니다: ${id}`)
    }

    // 3. 기존 데이터와 병합
    const existingRow = dataRows[rowIndex]!
    const existingUser = rowToUser(existingRow)
    const updated: User = {
      ...existingUser,
      ...input,
      id, // ID는 변경 불가
    }

    // 4. 시트 업데이트 (헤더 +1, 0-based index +1 = +2)
    const sheetRowIndex = rowIndex + 2
    const range = `A${sheetRowIndex}:F${sheetRowIndex}`
    await this.getClient().updateSheet(SHEET_NAMES.USERS, range, [userToRow(updated)])

    return updated
  }

  /**
   * 사용자 삭제 (Soft Delete)
   *
   * 실제로 데이터를 삭제하는 대신 isActive를 false로 설정합니다.
   * 이는 데이터 무결성을 유지하고, 필요시 복구할 수 있도록 합니다.
   */
  async delete(id: string): Promise<void> {
    // Soft delete: isActive를 false로 설정
    await this.update(id, { isActive: false })
  }
}
